// src/components/Pentests/tabs/tools/ModuleCombobox.js
//
// Custom dropdown for picking a scanning module. Each row shows:
//   * the module's display_name (bold)
//   * a one-line description (muted)
//   * an Active / Passive badge (red / green, glowing)
//
// Falls back gracefully when display_name is missing (e.g. older backend).

import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';

const ACTIVE_BADGE = 'bg-rose-100 text-rose-700 border-rose-300';
const PASSIVE_BADGE = 'bg-emerald-100 text-emerald-700 border-emerald-300';
const ACTIVE_GLOW = 'shadow-[0_0_10px_rgba(244,63,94,0.35)]';
const PASSIVE_GLOW = 'shadow-[0_0_10px_rgba(16,185,129,0.35)]';

function ScanTypeBadge({ type, glow = false }) {
  if (type === 'passive') {
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-[0.12em] ${PASSIVE_BADGE} ${glow ? PASSIVE_GLOW : ''}`}
      >
        Passive
      </span>
    );
  }
  if (type === 'active') {
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-[0.12em] ${ACTIVE_BADGE} ${glow ? ACTIVE_GLOW : ''}`}
      >
        Active
      </span>
    );
  }
  return null;
}

function moduleDisplayName(m) {
  return m?.display_name || (m?.name || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function ModuleCombobox({ modules, value, onChange, placeholder = 'Select a module', emptyMessage = 'No modules available' }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(
    () => modules.find((m) => m.name === value) || null,
    [modules, value],
  );

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return modules;
    return modules.filter((m) => {
      const title = moduleDisplayName(m).toLowerCase();
      const name = (m.name || '').toLowerCase();
      const desc = (m.description || '').toLowerCase();
      return title.includes(q) || name.includes(q) || desc.includes(q);
    });
  }, [modules, filter]);

  useEffect(() => {
    function onDocClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (open) {
      setHighlight(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    } else {
      setFilter('');
    }
  }, [open]);

  const select = (m) => {
    onChange(m.name);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (filtered[highlight]) {
        e.preventDefault();
        select(filtered[highlight]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const triggerLabel = selected ? moduleDisplayName(selected) : placeholder;

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full text-left rounded-lg border bg-cyber-100 px-3 py-2.5 transition outline-none ${
          open
            ? 'border-shinobi-green/70 ring-1 ring-shinobi-green/30'
            : 'border-cyber-300/60 hover:border-cyber-300'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={`text-sm font-semibold truncate ${selected ? 'text-slate-900' : 'text-slate-500'}`}>
                {triggerLabel}
              </span>
              {selected && <ScanTypeBadge type={selected.scan_type} glow />}
            </div>
            {selected?.description && (
              <p className="text-xs text-slate-500 truncate mt-0.5">{selected.description}</p>
            )}
          </div>
          <ChevronDown
            className={`w-4 h-4 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {open && (
        <div className="absolute z-30 mt-2 w-full rounded-xl border border-cyber-300/60 bg-cyber-50 shadow-[0_20px_60px_rgba(0,0,0,0.55)] backdrop-blur-sm overflow-hidden">
          <div className="px-3 py-2 border-b border-cyber-300/40 flex items-center gap-2 bg-cyber-100/40">
            <Search className="w-3.5 h-3.5 text-slate-500" />
            <input
              ref={inputRef}
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Filter modules..."
              className="flex-1 bg-transparent text-sm text-slate-900 placeholder-slate-500 outline-none"
            />
          </div>
          <div className="max-h-80 overflow-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-slate-500">
                {modules.length === 0 ? emptyMessage : 'No matches'}
              </div>
            ) : (
              filtered.map((m, idx) => {
                const isSelected = selected?.name === m.name;
                const isHighlight = idx === highlight;
                return (
                  <button
                    key={m.name}
                    type="button"
                    onMouseEnter={() => setHighlight(idx)}
                    onMouseDown={(e) => { e.preventDefault(); select(m); }}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 border-l-2 transition ${
                      isSelected
                        ? 'bg-wasabi-100 border-shinobi-green'
                        : isHighlight
                          ? 'bg-cyber-200/50 border-cyber-300/50'
                          : 'border-transparent hover:bg-cyber-200/40'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-semibold ${isSelected ? 'text-shinobi-green' : 'text-slate-900'} truncate`}>
                          {moduleDisplayName(m)}
                        </span>
                        {m.requires_api_key && (
                          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-300">
                            API Key
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                        {m.description}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0 pt-0.5">
                      <ScanTypeBadge type={m.scan_type} />
                      {isSelected && <Check className="w-3.5 h-3.5 text-shinobi-green" />}
                    </div>
                  </button>
                );
              })
            )}
          </div>
          <div className="px-3 py-2 border-t border-cyber-300/40 bg-cyber-100/30 flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-500">
            <span>{filtered.length} {filtered.length === 1 ? 'module' : 'modules'}</span>
            <span className="flex items-center gap-3">
              <span className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Passive
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-400" /> Active
              </span>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
